import { useState, useEffect, useMemo } from 'react'
import { customerService, inviteService } from '../services/api'
import { Send, Search, CheckCircle2, Clock, MousePointerClick, Mail, MessageCircle } from 'lucide-react'
import toast from 'react-hot-toast'

export default function Invites() {
  const [customers, setCustomers] = useState([])
  const [invites, setInvites]     = useState([])
  const [loading, setLoading]     = useState(true)
  const [search, setSearch]       = useState('')
  const [channel, setChannel]     = useState('email')
  const [sendingId, setSendingId] = useState(null)

  const load = async () => {
    setLoading(true)
    // NOTE: the admin api has a response interceptor (res => res.data), so these
    // calls return the DATA directly — NOT an axios response. Do not read .data again.
    try {
      const cdata = await customerService.getAll({ limit: 5000 })
      setCustomers(Array.isArray(cdata) ? cdata : (cdata?.items ?? cdata?.customers ?? []))
    } catch (e) {
      toast.error('Could not load customers')
    }
    try {
      const idata = await inviteService.list()
      setInvites(Array.isArray(idata) ? idata : (idata?.items ?? []))
    } catch (e) {
      setInvites([])
    }
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  // map latest invite status per customer_id
  const inviteByCustomer = useMemo(() => {
    const m = {}
    for (const inv of invites) {
      if (!m[inv.customer_id]) m[inv.customer_id] = inv   // list is newest-first
    }
    return m
  }, [invites])

  // customers WITHOUT a login yet are the ones to invite
  const invitable = useMemo(() => {
    const term = search.trim().toLowerCase()
    return customers
      .filter(c => !c.user_id)                            // no login account
      .filter(c => !term || (c.name || '').toLowerCase().includes(term) || (c.code || '').toLowerCase().includes(term))
      .slice(0, 500)
  }, [customers, search])

  const sendInvite = async (customer) => {
    setSendingId(customer.id)
    try {
      // create then send (interceptor returns data directly — no .data)
      const created = await inviteService.create({ customer_id: customer.id, channel })
      await inviteService.send(created.invite_id, { channel })
      toast.success(`Invite sent to ${customer.name}`)
      await load()
    } catch (e) {
      toast.error(e.message || 'Could not send invite')
    } finally {
      setSendingId(null)
    }
  }

  const statusBadge = (inv) => {
    if (!inv) return null
    const map = {
      sent:    { icon: <Clock className="w-3.5 h-3.5" />, cls: 'bg-blue-100 text-blue-700', label: 'Sent' },
      clicked: { icon: <MousePointerClick className="w-3.5 h-3.5" />, cls: 'bg-amber-100 text-amber-700', label: 'Clicked' },
      claimed: { icon: <CheckCircle2 className="w-3.5 h-3.5" />, cls: 'bg-green-100 text-green-700', label: 'Claimed' },
      pending: { icon: <Clock className="w-3.5 h-3.5" />, cls: 'bg-gray-700 text-gray-300', label: 'Pending' },
      expired: { icon: <Clock className="w-3.5 h-3.5" />, cls: 'bg-red-100 text-red-600', label: 'Expired' },
    }
    const s = map[inv.status] || map.pending
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>{s.icon}{s.label}</span>
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Customer Invitations</h1>
        <p className="text-sm text-gray-500 mt-1">
          Send onboarding invites to customers who don't have a login yet. They receive a username,
          a 6-digit code, and a claim link to set their password.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or code…"
            className="w-full pl-9 pr-3 py-2 border border-surface-border rounded-lg bg-surface-card text-white placeholder-gray-500 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Send via:</span>
          <select value={channel} onChange={e => setChannel(e.target.value)}
            className="border border-surface-border rounded-lg bg-surface-card text-white px-3 py-2 text-sm">
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="both">Both</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500 py-8 text-center">Loading…</p>
      ) : (
        <div className="bg-surface-card rounded-lg border border-surface-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/30 text-gray-400 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {invitable.map(c => {
                const inv = inviteByCustomer[c.id]
                const canContact = (channel === 'email' && c.email) || (channel === 'whatsapp' && c.phone) || (channel === 'both' && (c.email || c.phone))
                return (
                  <tr key={c.id} className="border-t border-surface-border/50 hover:bg-white/[0.03]">
                    <td className="px-4 py-3 text-gray-500">{c.code}</td>
                    <td className="px-4 py-3 font-medium text-white">{c.name}</td>
                    <td className="px-4 py-3 text-gray-500">
                      <div className="flex flex-col gap-0.5">
                        {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                        {c.phone && <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" />{c.phone}</span>}
                        {!c.email && !c.phone && <span className="text-red-400 text-xs">no contact details</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">{statusBadge(inv) || <span className="text-gray-600 text-xs">—</span>}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => sendInvite(c)}
                        disabled={sendingId === c.id || !canContact}
                        className="admin-btn-solid inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
                        title={!canContact ? 'No contact detail for this channel' : 'Send invite'}
                      >
                        <Send className="w-3.5 h-3.5" />
                        {sendingId === c.id ? 'Sending…' : (inv ? 'Resend' : 'Send invite')}
                      </button>
                    </td>
                  </tr>
                )
              })}
              {invitable.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                  No customers without logins match your search.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-4">
        Showing customers without a login account (up to 500). Sending creates a fresh 6-digit code each time
        (re-sending regenerates the code). Claimed customers drop off this list once they set their password.
      </p>
    </div>
  )
}
